import { useMutation, useQueryClient } from "@tanstack/react-query";
import { UserProfileDto, UserRole } from "@green0meter/api-client";
import { useTranslation } from "react-i18next";
import { useSnackbar } from "notistack";

import { DOMAIN_DOUBLE_MATERIALITY } from "../domain";
import { ACTION_GET_STAKEHOLDERS } from "../GetDMStakeholders/useGetDMStakeholders";

import { roleGuard } from "@api";
import { doubleMaterialityStakeholdersApi } from "@api/services";

export const ACTION_DELETE_STAKEHOLDER = "DELETE_STAKEHOLDER";

const allowedRoles = [UserRole.Owner, UserRole.Manager];

const deleteStakeholder = async (id: string): Promise<void> => {
  await doubleMaterialityStakeholdersApi.doubleMaterialityStakeholdersControllerDeleteStakeholder(
    id,
  );
};

export const useDeleteStakeholder = (
  user: UserProfileDto | null,
): {
  deleteStakeholder: (id: string) => void;
  deleteStakeholderAsync: (id: string) => Promise<void>;
  isPendingDeleteStakeholder: boolean;
  isSuccessDeleteStakeholder: boolean;
  isErrorDeleteStakeholder: boolean;
  errorDeleteStakeholder: Error | null;
  enabled: boolean;
} => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();
  const enabled = roleGuard(user, allowedRoles);

  const mutation = useMutation({
    mutationFn: deleteStakeholder,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [DOMAIN_DOUBLE_MATERIALITY, ACTION_GET_STAKEHOLDERS],
      });
      enqueueSnackbar(t("stakeholders.messages.deleteSuccess"), {
        variant: "success",
      });
    },
    onError: (error: Error) => {
      enqueueSnackbar(error.message || t("stakeholders.messages.deleteError"), {
        variant: "error",
      });
    },
  });

  return {
    deleteStakeholder: mutation.mutate,
    deleteStakeholderAsync: mutation.mutateAsync,
    isPendingDeleteStakeholder: mutation.isPending,
    isSuccessDeleteStakeholder: mutation.isSuccess,
    isErrorDeleteStakeholder: mutation.isError,
    errorDeleteStakeholder: mutation.error,
    enabled,
  };
};
